'use client'

import { useCallback, useEffect, useState } from 'react'
import { MdTune, MdVolumeOff, MdVolumeUp } from 'react-icons/md'
import { toast } from 'react-toastify'

import {
    getPersonalAlertPreferences,
    updatePersonalAlertPreferences,
    type PersonalAlertPreference,
} from '@/lib/api/notifications/notification-api'
import { useResourceVisibility } from '@/lib/hooks/use-entity-access'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'

interface MyAlertsPreferencesProps {
    /** Acompanha o estado do botão "Meus Alertas" ao lado, para os dois parecerem um só. */
    highlighted: boolean
    /** Som de mensagem nova; o estado vive em `useMessageAlertSound`, no dropdown. */
    soundEnabled: boolean
    soundSaving: boolean
    onToggleSound: () => void
}

/**
 * Escolha dos alertas que chegam em "Meus Alertas".
 *
 * Nasceu da reclamação de 09/10/2026: a equipe recebia mais aviso de lead, oportunidade
 * e carteira do que de mensagem, que é o que a maioria quer. Cada pessoa liga e
 * desliga por tipo; a escolha vale para os PRÓXIMOS alertas.
 *
 * Só aparecem os tipos cujo recurso a pessoa enxerga no menu: oferecer "Aprovações do
 * financeiro" a quem não tem o financeiro seria uma opção que nunca faz nada.
 */
export default function MyAlertsPreferences({
    highlighted,
    soundEnabled,
    soundSaving,
    onToggleSound,
}: MyAlertsPreferencesProps) {
    const isResourceVisible = useResourceVisibility()
    const [open, setOpen] = useState(false)
    const [preferences, setPreferences] = useState<PersonalAlertPreference[]>([])
    const [loading, setLoading] = useState(false)
    const [savingKey, setSavingKey] = useState<string | null>(null)

    const { anchorRef, renderPanel } = useAnchoredDropdown(open, () => setOpen(false), {
        matchAnchorWidth: false,
    })

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await getPersonalAlertPreferences()
            if (res.success) setPreferences(res.result ?? [])
            else toast.error(res.error || 'Não foi possível carregar suas preferências de alerta.')
        } finally {
            setLoading(false)
        }
    }, [])

    // Carrega ao abrir: a escolha pode ter mudado em outra aba ou aparelho.
    useEffect(() => {
        if (open) void load()
    }, [open, load])

    const visible = preferences.filter(
        preference =>
            !preference.module || !preference.resource || isResourceVisible(preference.module, preference.resource),
    )

    const toggle = async (key: string) => {
        const previous = preferences
        const next = preferences.map(preference =>
            preference.key === key ? { ...preference, enabled: !preference.enabled } : preference,
        )
        // Otimista: a caixa responde ao clique; se o servidor recusar, volta como estava.
        setPreferences(next)
        setSavingKey(key)
        try {
            const res = await updatePersonalAlertPreferences(
                next.filter(preference => !preference.enabled).map(preference => preference.key),
            )
            if (res.success && res.result) {
                setPreferences(res.result)
            } else {
                setPreferences(previous)
                toast.error(res.error || 'Não foi possível salvar sua preferência de alerta.')
            }
        } finally {
            setSavingKey(null)
        }
    }

    return (
        <>
            <div ref={anchorRef} className="relative">
                <button
                    type="button"
                    onClick={() => setOpen(value => !value)}
                    className={`btn px-2 rounded-l-none border-l ${
                        highlighted
                            ? 'btn-primary !text-white border-l-white/30'
                            : 'btn-ghost border-l-base-content/10'
                    }`}
                    title="Escolher os alertas que você recebe"
                    aria-label="Escolher os alertas que você recebe"
                    aria-expanded={open}
                >
                    <MdTune className="text-lg" />
                </button>
            </div>

            {renderPanel(
                <div className="w-80 p-3">
                    <label
                        className={`mb-3 flex cursor-pointer items-center gap-3 rounded-lg border border-base-200 px-3 py-2.5 hover:bg-base-200/60 ${
                            soundSaving ? 'opacity-60' : ''
                        }`}
                    >
                        {soundEnabled ? (
                            <MdVolumeUp className="text-xl flex-shrink-0 text-primary" />
                        ) : (
                            <MdVolumeOff className="text-xl flex-shrink-0 text-base-content/40" />
                        )}
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="text-sm font-medium text-base-content">Som de mensagem nova</span>
                            <span className="text-xs leading-snug text-base-content/60">
                                Toca quando chega mensagem numa conversa sua.
                            </span>
                        </span>
                        <input
                            type="checkbox"
                            className="toggle toggle-primary toggle-sm"
                            checked={soundEnabled}
                            disabled={soundSaving}
                            onChange={onToggleSound}
                        />
                    </label>

                    <div className="mb-3">
                        <h3 className="text-sm font-semibold text-base-content">Alertas que você recebe</h3>
                        <p className="mt-0.5 text-xs text-base-content/60">
                            Desmarque o que não quer receber. Vale para os próximos alertas.
                        </p>
                    </div>

                    {loading && preferences.length === 0 ? (
                        <div className="flex justify-center py-6">
                            <span className="loading loading-spinner loading-sm" />
                        </div>
                    ) : visible.length === 0 ? (
                        <p className="py-6 text-center text-sm text-base-content/50">
                            Nenhum tipo de alerta disponível.
                        </p>
                    ) : (
                        <ul className="flex flex-col gap-1">
                            {visible.map(preference => (
                                <li key={preference.key}>
                                    <label
                                        className={`flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-base-200/60 ${
                                            savingKey === preference.key ? 'opacity-60' : ''
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-primary checkbox-sm mt-0.5"
                                            checked={preference.enabled}
                                            disabled={savingKey !== null}
                                            onChange={() => void toggle(preference.key)}
                                        />
                                        <span className="flex min-w-0 flex-col gap-0.5">
                                            <span className="text-sm font-medium text-base-content">
                                                {preference.label}
                                            </span>
                                            <span className="text-xs leading-snug text-base-content/60">
                                                {preference.description}
                                            </span>
                                        </span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>,
                'overflow-x-hidden',
            )}
        </>
    )
}
